import { isEqual } from 'lodash-es';
import {
  forwardRef,
  type HTMLAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { cn } from '../../../utils';

import { advancedFiltersReducer } from './AdvancedFilters.reducer';
import { AdvancedFilterRow } from './components/AdvancedFilterRow';
import { AdvancedFiltersAddConditionButton } from './components/AdvancedFiltersAddConditionButton';
import { AdvancedFiltersClearAllButton } from './components/AdvancedFiltersClearAllButton';
import { AdvancedFiltersConditionCombineMode } from './components/AdvancedFiltersConditionCombineMode';
import { AdvancedFiltersInternalContext } from './contexts/advanced-filters-internal.context';
import { useAdvancedFiltersPublicContext } from './contexts/advanced-filters-public.context';
import { type AdvancedFiltersPublicState } from './types';
import { type AdvancedFilterCondition } from './types/config.types';
import {
  advancedFilterConditionIsComplete,
  initialiseAdvancedFiltersReducer,
} from './utilities/reducer-utils';

export interface AdvancedFiltersContentProps
  extends HTMLAttributes<HTMLDivElement> {
  alwaysCommit?: boolean;
}

export interface AdvancedFiltersContentRef extends HTMLDivElement {
  commit: () => void;
}

export const AdvancedFiltersContent = forwardRef<
  AdvancedFiltersContentRef,
  AdvancedFiltersContentProps
>(({ className, alwaysCommit = false, ...props }, ref) => {
  const { config, defaultDraftAdvancedFilters, onCommitDraftAdvancedFilters } =
    useAdvancedFiltersPublicContext();

  const prevCompletedState = useRef<AdvancedFiltersPublicState>(
    defaultDraftAdvancedFilters,
  );

  const [state, dispatch] = useReducer(
    advancedFiltersReducer,
    defaultDraftAdvancedFilters,
    initialiseAdvancedFiltersReducer,
  );

  const completedState = useMemo(() => {
    const conditionsWithIds = Object.entries(state.conditions)
      .filter((entry): entry is [string, AdvancedFilterCondition] =>
        advancedFilterConditionIsComplete(entry[1]),
      )
      .map(([rowId, condition]) => ({
        ...condition,
        id: rowId,
      }));

    return {
      conditionCombineMode: state.conditionCombineMode,
      conditions: conditionsWithIds,
    };
  }, [state]);

  const syncAdvancedFiltersState = useCallback(() => {
    if (isEqual(completedState, prevCompletedState.current)) {
      return;
    }
    onCommitDraftAdvancedFilters(completedState);
    prevCompletedState.current = completedState;
  }, [completedState, onCommitDraftAdvancedFilters]);

  const [divNode, setDivNode] = useState<HTMLDivElement | null>(null);
  useImperativeHandle(
    ref,
    () => ({
      ...(divNode as HTMLDivElement),
      commit: syncAdvancedFiltersState,
    }),
    [divNode, syncAdvancedFiltersState],
  );

  useEffect(() => {
    if (alwaysCommit) {
      syncAdvancedFiltersState();
    }
  }, [alwaysCommit, completedState, syncAdvancedFiltersState]);

  const { conditions } = state;

  return (
    <AdvancedFiltersInternalContext.Provider
      value={{ state, dispatch, config }}
    >
      <div
        className={cn('flex flex-col gap-4', className)}
        {...props}
        ref={setDivNode}
      >
        <div className="flex flex-col gap-2">
          {Object.keys(conditions).map((rowId) => (
            <AdvancedFilterRow key={rowId} rowId={rowId} />
          ))}
        </div>
        <div className="flex justify-between">
          <AdvancedFiltersConditionCombineMode />
          <div className="flex gap-2">
            <AdvancedFiltersClearAllButton />
            <AdvancedFiltersAddConditionButton />
          </div>
        </div>
      </div>
    </AdvancedFiltersInternalContext.Provider>
  );
});
